/**
 * Household Detail View — SPA component for the Vite + React Router app.
 * Self-contained version of the household detail page that takes householdId and onBack props.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { householdsAPI, Household } from '@/services/api/households';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import RecordFieldGroupsSection from '@/components/records/RecordFieldGroupsSection';
import {
  ArrowLeft,
  Users,
  AlertTriangle,
  ClipboardList,
  Calendar,
  Clock,
  MapPin,
  Home,
  Loader2,
} from 'lucide-react';
import HouseholdMembersTab from './HouseholdMembersTab';
import HouseholdNeedsTab from './HouseholdNeedsTab';
import HouseholdServicesTab from './HouseholdServicesTab';
import HouseholdFollowUpsTab from './HouseholdFollowUpsTab';
import HouseholdTimelineTab from './HouseholdTimelineTab';
import HouseholdEpisodesTab from './HouseholdEpisodesTab';
import { FileText } from 'lucide-react';

const DISPLACEMENT_LABELS: Record<string, { label: string; color: string }> = {
  DISPLACED: { label: 'Displaced', color: 'bg-red-100 text-red-800' },
  TEMPORARY_HOUSING: { label: 'Temporary Housing', color: 'bg-orange-100 text-orange-800' },
  PERMANENT_REHOUSED: { label: 'Permanently Rehoused', color: 'bg-blue-100 text-blue-800' },
  RETURNED: { label: 'Returned', color: 'bg-green-100 text-green-800' },
};

interface Props {
  householdId: number;
  onBack: () => void;
}

export default function HouseholdDetailView({ householdId, onBack }: Props) {
  const [household, setHousehold] = useState<Household | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('members');

  const loadHousehold = useCallback(async () => {
    try {
      const data = await householdsAPI.getHousehold(householdId);
      setHousehold(data);
    } catch (err) {
      console.error('Failed to load household:', err);
    } finally {
      setLoading(false);
    }
  }, [householdId]);

  useEffect(() => {
    loadHousehold();
  }, [loadHousehold]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-indigo-600" />
        <span className="ml-2 text-gray-500">Loading household...</span>
      </div>
    );
  }

  if (!household) {
    return (
      <div className="text-center py-20">
        <Home className="w-12 h-12 text-gray-300 mx-auto mb-3" />
        <h3 className="text-lg font-medium text-gray-900">Household not found</h3>
        <Button variant="outline" onClick={onBack} className="mt-4">
          Back
        </Button>
      </div>
    );
  }

  const displacementCfg = DISPLACEMENT_LABELS[household.displacement_status || ''];
  const address = [household.address_line1, household.city, household.state, household.zip_code]
    .filter(Boolean)
    .join(', ');
  const members = household.members || [];
  const headOfHousehold = members.find((m) => m.role_in_household === 'HEAD_OF_HOUSEHOLD');

  return (
    <div className="space-y-6 p-4 lg:p-6 max-w-7xl mx-auto">
      {/* Back + Header */}
      <div className="flex items-start gap-4">
        <Button variant="ghost" size="sm" onClick={onBack} className="mt-1">
          <ArrowLeft className="w-4 h-4 mr-1" />
          Back
        </Button>
        <div className="flex-1">
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-2xl font-bold text-gray-900">{household.household_number}</h1>
            <Badge
              className={
                household.status === 'ACTIVE'
                  ? 'bg-green-100 text-green-800'
                  : household.status === 'CLOSED'
                  ? 'bg-gray-100 text-gray-800'
                  : 'bg-yellow-100 text-yellow-800'
              }
              variant="secondary"
            >
              {household.status}
            </Badge>
            {displacementCfg && (
              <Badge className={displacementCfg.color} variant="secondary">
                {displacementCfg.label}
              </Badge>
            )}
          </div>
          {address && (
            <p className="text-sm text-gray-500 mt-1 flex items-center gap-1">
              <MapPin className="w-3.5 h-3.5" />
              Pre-disaster: {address}
              {household.county && ` (${household.county} County)`}
            </p>
          )}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
              <Home className="w-4 h-4 text-gray-400" />
              Property
            </div>
            <p className="text-sm">
              {household.property_type === 'OWNED'
                ? 'Owned'
                : household.property_type === 'RENTED'
                ? 'Rented'
                : household.property_type || 'Not specified'}
            </p>
            <p className="text-xs text-gray-500 mt-1">
              {household.had_insurance
                ? `Insured: ${household.insurance_provider || household.insurance_type || 'Yes'}`
                : 'No insurance'}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
              <AlertTriangle className="w-4 h-4 text-gray-400" />
              Disaster
            </div>
            <p className="text-sm">{household.disaster_event || 'Not specified'}</p>
            {household.disaster_date && (
              <p className="text-xs text-gray-500 mt-1">
                Date: {new Date(household.disaster_date).toLocaleDateString()}
              </p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
              <Users className="w-4 h-4 text-gray-400" />
              Members
            </div>
            <p className="text-sm">
              {members.length} member{members.length !== 1 ? 's' : ''}
            </p>
            {headOfHousehold && (
              <p className="text-xs text-gray-500 mt-1">
                Head: {headOfHousehold.first_name} {headOfHousehold.last_name}
              </p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
              <Calendar className="w-4 h-4 text-gray-400" />
              Case
            </div>
            <p className="text-sm">
              Opened:{' '}
              {household.case_opened_date
                ? new Date(household.case_opened_date).toLocaleDateString()
                : 'N/A'}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-6 w-full max-w-3xl">
          <TabsTrigger value="members" className="gap-1.5">
            <Users className="w-3.5 h-3.5" />
            Members
          </TabsTrigger>
          <TabsTrigger value="needs" className="gap-1.5">
            <AlertTriangle className="w-3.5 h-3.5" />
            Needs
          </TabsTrigger>
          <TabsTrigger value="services" className="gap-1.5">
            <ClipboardList className="w-3.5 h-3.5" />
            Services
          </TabsTrigger>
          <TabsTrigger value="episodes" className="gap-1.5">
            <FileText className="w-3.5 h-3.5" />
            Episodes
          </TabsTrigger>
          <TabsTrigger value="follow-ups" className="gap-1.5">
            <Calendar className="w-3.5 h-3.5" />
            Follow-ups
          </TabsTrigger>
          <TabsTrigger value="timeline" className="gap-1.5">
            <Clock className="w-3.5 h-3.5" />
            Timeline
          </TabsTrigger>
        </TabsList>

        <TabsContent value="members" className="mt-4 space-y-4">
          <HouseholdMembersTab
            householdId={householdId}
            members={members}
            onRefresh={loadHousehold}
          />
          {/* TRUS-402: repeatable field groups placed on households. Renders
              nothing until an admin sets a group's "Appears on" to Households,
              so an org that uses none sees no change. */}
          <RecordFieldGroupsSection recordType="household" householdId={householdId} />
        </TabsContent>

        <TabsContent value="needs" className="mt-4">
          <HouseholdNeedsTab householdId={householdId} members={members} />
        </TabsContent>

        <TabsContent value="services" className="mt-4">
          <HouseholdServicesTab householdId={householdId} members={members} />
        </TabsContent>

        <TabsContent value="follow-ups" className="mt-4">
          <HouseholdFollowUpsTab householdId={householdId} members={members} />
        </TabsContent>

        <TabsContent value="episodes" className="mt-4">
          <HouseholdEpisodesTab
            householdId={householdId}
            cboId={parseInt(localStorage.getItem('active_cbo_id') || '0', 10)}
          />
        </TabsContent>

        <TabsContent value="timeline" className="mt-4">
          <HouseholdTimelineTab householdId={householdId} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
